import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Play, Check, Clock, ChevronDown,
  LayoutDashboard, Activity, Gauge, Upload, BarChart3, FileText,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import { goToApp } from "@/lib/domains";
import { LeadDialog } from "@/components/landing/LeadDialog";
import { logEvent } from "@/lib/analytics";
import { markDemoStarted, trackDemoPercent } from "@/lib/demoProgress";
import { DEMO_VIDEO_URL, DEMO_POSTER, DEMO_DURATION, DEMO_SECTIONS, DEMO_FAQS, DEMO_SEO, isYouTube } from "@/lib/demo";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const ICONS = { LayoutDashboard, Activity, Gauge, Upload, BarChart3, FileText };

function useDemoSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = DEMO_SEO.title;
    const metas = [];
    const set = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); metas.push(el); }
      el.setAttribute("content", val);
    };
    set("name", "description", DEMO_SEO.description);
    set("property", "og:title", DEMO_SEO.title);
    set("property", "og:description", DEMO_SEO.description);
    set("property", "og:type", "video.other");
    set("property", "og:image", DEMO_POSTER);
    set("name", "twitter:card", "summary_large_image");
    set("name", "twitter:title", DEMO_SEO.title);
    set("name", "twitter:description", DEMO_SEO.description);
    set("name", "twitter:image", DEMO_POSTER);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "VideoObject",
      name: "RecoverOS Product Demo", description: DEMO_SEO.description,
      thumbnailUrl: [DEMO_POSTER], uploadDate: "2026-01-01",
      contentUrl: DEMO_VIDEO_URL || undefined, publisher: { "@type": "Organization", name: "RecoverOS" },
    });
    document.head.appendChild(ld);
    return () => { document.title = prev; metas.forEach((m) => m.remove()); ld.remove(); };
  }, []);
}

function FaqRow({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white" data-testid={`demo-faq-${idx}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-semibold text-slate-900" style={JK}>{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#0B3D91] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600">{a}</p>}
    </div>
  );
}

export default function Demo() {
  useDemoSeo();
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [lead, setLead] = useState({ open: false, kind: "demo", plan: "" });
  const appNav = (path) => goToApp(path, navigate);
  const hasVideo = !!DEMO_VIDEO_URL;

  const startPlay = () => { markDemoStarted("demo-page"); setPlaying(true); };
  const onTime = (e) => { const v = e.target; if (v.duration) trackDemoPercent(Math.round((v.currentTime / v.duration) * 100), "demo-page"); };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={{ fontFamily: "'Inter', sans-serif" }}>
      <LeadDialog open={lead.open} onOpenChange={(o) => setLead((s) => ({ ...s, open: o }))} kind={lead.kind} plan={lead.plan} />

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 flex items-center justify-between h-16">
          <button onClick={() => navigate("/?stay=1")} className="flex items-center gap-2" data-testid="demo-brand">
            <Brand iconSize={32} textClass="text-lg" style={JK} />
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/?stay=1")} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0B3D91] transition-colors px-3 py-2" data-testid="demo-back">
              <ArrowLeft className="h-4 w-4" /> Back to home
            </button>
            <button onClick={() => appNav("/signup")} className="inline-flex bg-[#16A34A] text-white rounded-none px-4 py-2.5 text-sm font-semibold hover:bg-[#138a3e] transition-colors" data-testid="demo-nav-trial">Get Started</button>
          </div>
        </div>
      </header>

      {/* HERO / PLAYER */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 py-14 lg:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">
              <Clock className="h-3.5 w-3.5" /> {DEMO_DURATION} product walkthrough
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-slate-950" style={JK}>
              See RecoverOS in Action
            </h1>
            <p className="mt-4 text-base lg:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Understand how RecoverOS helps organizations recover more money in just a few minutes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-10 relative mx-auto max-w-4xl border border-slate-200 bg-slate-900 overflow-hidden shadow-[0_40px_80px_-24px_rgba(11,61,145,0.45)]"
            data-testid="demo-player"
          >
            <div className="relative aspect-video">
              {playing && hasVideo ? (
                isYouTube(DEMO_VIDEO_URL) ? (
                  <iframe className="h-full w-full" src={DEMO_VIDEO_URL + "?autoplay=1&rel=0"} title="RecoverOS Demo" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="demo-iframe" />
                ) : (
                  <video className="h-full w-full" src={DEMO_VIDEO_URL} poster={DEMO_POSTER} controls autoPlay onTimeUpdate={onTime} data-testid="demo-video" />
                )
              ) : (
                <button onClick={hasVideo ? startPlay : undefined} className="absolute inset-0 group" data-testid="demo-play-btn" aria-label="Play demo">
                  <img src={DEMO_POSTER} alt="RecoverOS demo preview" className="h-full w-full object-cover opacity-70" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-6" style={{ background: "rgba(11,61,145,0.55)" }}>
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-[#0B3D91] transition-transform group-hover:scale-110">
                      <Play size={34} fill="currentColor" />
                    </div>
                    {!hasVideo && (
                      <>
                        <h3 className="mt-5 text-xl font-bold text-white" style={JK}>Full {DEMO_DURATION} walkthrough coming soon</h3>
                        <p className="mt-1 max-w-md text-sm text-white/85">Meanwhile, start free with Intro PAYG or book a live demo with our team.</p>
                      </>
                    )}
                  </div>
                </button>
              )}
            </div>
          </motion.div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => { logEvent("demo_cta_clicked", { cta: "trial", source: "demo-page" }); appNav("/signup"); }} className="group bg-[#16A34A] text-white rounded-none px-6 py-3.5 font-semibold hover:bg-[#138a3e] hover:-translate-y-0.5 transition-all flex items-center gap-2" data-testid="demo-cta-trial">
              Start Free — Intro PAYG <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => { logEvent("book_demo_clicked", { source: "demo-page" }); setLead({ open: true, kind: "demo", plan: "" }); }} className="border-2 border-[#0B3D91] bg-white text-[#0B3D91] rounded-none px-6 py-3.5 font-semibold hover:bg-[#0B3D91]/[0.04] hover:-translate-y-0.5 transition-all" data-testid="demo-cta-book">
              Book Live Demo
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">₹0 for 3 months · ready in 2 minutes</p>
        </div>
      </section>

      {/* PRODUCT OVERVIEW */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">Product Overview</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>What you'll see in the demo</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {DEMO_SECTIONS.map((s, i) => {
              const Icon = ICONS[s.icon] || LayoutDashboard;
              return (
                <div key={s.title} className="bg-white p-7 hover:bg-slate-50 transition-colors" data-testid={`demo-section-${i}`}>
                  <div className="h-11 w-11 bg-[#0B3D91]/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#0B3D91]" />
                  </div>
                  <div className="mt-4 font-bold text-slate-900" style={JK}>{s.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B3D91]">FAQ</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Demo questions, answered</h2>
          </div>
          <div className="mt-10 space-y-3">
            {DEMO_FAQS.map((f, i) => <FaqRow key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950" style={JK}>Ready to see your own numbers?</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {["No subscription for 3 months", "Up to 100 accounts · 1 user", "Up to 250 Email Communications", "Includes up to 50 Free RC/month"].map((b) => (
              <span key={b} className="flex items-center gap-2 text-slate-700 font-medium"><Check className="h-5 w-5 text-[#16A34A]" />{b}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button onClick={() => appNav("/signup")} className="bg-[#16A34A] text-white rounded-none px-8 py-4 font-semibold hover:bg-[#138a3e] transition-colors" data-testid="demo-closing-trial">Start with Intro PAYG</button>
            <button onClick={() => setLead({ open: true, kind: "demo", plan: "" })} className="border-2 border-slate-300 text-slate-900 rounded-none px-8 py-4 font-semibold hover:border-[#0B3D91] hover:text-[#0B3D91] transition-colors" data-testid="demo-closing-book">Book Demo</button>
          </div>
        </div>
      </section>
    </div>
  );
}
